import { useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Modal, ModalFooter, Btn, useToast } from '../ui';
import { InlineSearchPicker, useDealPickerItems } from '../tasks/TaskComponents';

// ── File an email to a deal ─────────────────────────────────────────────────
// ONE dialog behind both surfaces that can do it — the Mail page's reading
// pane and a Daily Brief mail row — so the two cannot file an email
// differently. It posts to POST /api/mail/messages/{id}/file-to-deal, which
// runs the SAME pipeline the forward-to-mailbox capture and the Outlook add-in
// use: a summary note on the deal, each attachment landed as a deal document,
// the leads told once, and the message stamped "Filed to Harvus" in Outlook.
//
// The request runs a Claude summary and downloads the attachments, so it gets
// a long timeout; a timeout is reported as "may still be filing" because the
// route is idempotent — pressing it again returns the note it already made.

export function fileToDealPath(messageId, mailbox) {
  const base = `mail/messages/${encodeURIComponent(messageId)}/file-to-deal`;
  return mailbox ? `${base}?mailbox=${encodeURIComponent(mailbox)}` : base;
}

export function describeFiled(r) {
  if (!r) return 'Filed.';
  if (r.alreadyFiled) return `Already filed to ${r.label || 'that deal'} — nothing new was added.`;
  const parts = [`Filed to ${r.label || 'the deal'}`];
  if (r.documentCount) parts.push(`${r.documentCount} attachment${r.documentCount === 1 ? '' : 's'} added to Documents`);
  if (r.skippedAttachments?.length) parts.push(`${r.skippedAttachments.length} attachment${r.skippedAttachments.length === 1 ? '' : 's'} left out`);
  return `${parts.join(' · ')}.`;
}

export function FileToDealModal({ messageId, subject, mailbox, suggestedDealId, onClose, onFiled }) {
  const { dealPickerItems, dealPickerError } = useDealPickerItems();
  const [combo, setCombo] = useState(suggestedDealId ? `deal:${suggestedDealId}` : '');
  const [markFiled, setMarkFiled] = useState(true);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const file = async () => {
    if (!combo) return;
    const [kind, id] = combo.split(':');
    const body = kind === 'detail' ? { dealDetailId: id } : { dealId: id };
    setBusy(true);
    try {
      const r = await api.post(fileToDealPath(messageId, mailbox), { ...body, markFiled }, { timeout: 90000 });
      toast.success(describeFiled(r));
      onFiled?.(r);
      onClose();
    } catch (e) {
      const timedOut = /timed out/i.test(e?.message || '') || (e?.status >= 500);
      toast.error(timedOut
        ? 'That is taking a while — it may still be filing. Check the deal, or press File again (it will not file twice).'
        : (e?.message || 'Could not file that email.'));
    } finally { setBusy(false); }
  };

  return (
    <Modal title="File email to a deal" onClose={onClose} width={520}>
      {subject && <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>{subject}</div>}
      <InlineSearchPicker
        label="Deal or capital item"
        value={combo}
        onChange={setCombo}
        items={dealPickerItems}
        placeholder="Search deals & details…"
        clearLabel="— No deal —"
      />
      {dealPickerError && <div style={{ fontSize: 12, color: B.red, marginTop: 6 }}>Could not load deals.</div>}
      <div style={{ fontSize: 12.5, color: B.textMd, marginTop: 12, lineHeight: 1.5 }}>
        A summary of the email becomes a note on the deal, the email itself is attached to it as a PDF,
        and each attachment is added to the deal's Documents. The deal's leads are told once.
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: B.text, marginTop: 12 }}>
        <input type="checkbox" checked={markFiled} onChange={(e) => setMarkFiled(e.target.checked)} />
        Tag it "Filed to Harvus" in Outlook
      </label>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={file} disabled={!combo || busy}>{busy ? 'Filing…' : 'File'}</Btn>
      </ModalFooter>
    </Modal>
  );
}
